import { forwardRef, useRef, useState } from "react";
import { Section } from "../components/Layout/Section";
import { Container } from "../components/Layout/Container";
import styled from "@emotion/styled";
import SectionTitle from "../components/SectionTitle";
import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { IsBoolean, IsEmail, IsString, ValidateIf } from "class-validator";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { logEvent } from "@firebase/analytics";
import SubmitButton from "../components/SubmitButton";
import Alert from "../components/Alert";
import { useApp } from "../AppProvider";
import { httpsCallable } from "@firebase/functions";
import ReCAPTCHA from "react-google-recaptcha";

const FormularWrapper = styled("div")(() => ({
  marginBottom: 30,
  display: "grid",
  width: "100%",
  gridTemplateColumns: "repeat(1, 1fr)",
  "@media (min-width: 768px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  gap: "1rem",
}));

const Label = styled("label")(() => ({
  fontSize: 16,
  display: "block",
  gridColumn: "span 1",
  "& span": {
    display: "block",
  },
}));

const FullsizeLabel = styled(Label)(() => ({
  gridColumn: "span 1",
  "@media (min-width: 768px)": {
    gridColumn: "span 2",
  },
}));

const Input = styled("input")(() => ({
  backgroundColor: "rgba(0,0,0,0.05)",
  border: "none",
  padding: "10px",
  outline: "none",
  width: "100%",
  marginTop: 10,
  fontSize: 16,
  borderRadius: 8,
}));

const Textarea = styled("textarea")(() => ({
  backgroundColor: "rgba(0,0,0,0.05)",
  border: "none",
  padding: "10px",
  outline: "none",
  width: "100%",
  marginTop: 10,
  fontSize: 16,
  fontFamily: "inherit",
  borderRadius: 8,
}));

const LOCATION =
  "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d20050.91334841654!2d13.698575481621228!3d51.083286442317345!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4709ce29b96b68bb%3A0xdf938bdf34cde2c3!2sCentre%20Films%20GmbH%20%26%20Co.KG!5e0!3m2!1sde!2sde!4v1666808473656!5m2!1sde!2sde";

const Frame = styled("iframe")(() => ({
  border: "none",
  width: "100%",
  height: 450,
  borderRadius: 20,
}));

const Link = styled("a")(() => ({
  border: "none",
  outline: "none",
  backgroundColor: "transparent",
  cursor: "pointer",
  color: "#1a73e8",
  margin: 0,
  padding: 0,
  display: "inline",
  fontWeight: "inherit",
  fontSize: "inherit",
}));

const CheckboxInput = styled(Input)(() => ({
  width: "auto",
  margin: "0 10px 0 0",
}));

const Subtitle = styled("h3")(() => ({
  fontWeight: "normal",
}));

class IKontaktformular {
  @IsString({ message: "Bitte geben Sie einen Namen ein." })
  name: string;

  @IsEmail({}, { message: "Bitte geben Sie eine E-Mail-Adresse ein." })
  email: string;

  @ValidateIf((o) => o.message !== undefined)
  @IsString({ message: "Bitte geben Sie eine Nachricht ein." })
  message: string;

  @IsBoolean()
  privacyAccepted: boolean;
}

const Divider = styled("div")(() => ({
  width: "100%",
  height: 0,
  borderTopWidth: 1,
  borderTopColor: "rgba(0,0,0,0.1)",
  borderTopStyle: "solid",
  margin: "40px 0",
}));

const ChefsWrapper = styled("div")(() => ({
  display: "grid",
  gridTemplateColumns: "repeat(1, 1fr)",
  gap: "2rem",
  width: "100%",
  "@media (min-width: 768px)": {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  "@media (min-width: 1024px)": {
    gridTemplateColumns: "repeat(3, 1fr)",
  },
  "@media (min-width: 1280px)": {
    gridTemplateColumns: "repeat(4, 1fr)",
  },
}));

const ChefsItem = styled(motion.div)(() => ({
  border: "1px solid rgba(0,0,0,0.1)",
  padding: "1rem",
  gridColumn: "span 1",
  display: "flex",
  flexDirection: "column",
  gap: "0.8rem",
  borderRadius: 12,
  textDecoration: "none",
  cursor: "pointer",
}));

const ChefsLink = styled("a")(() => ({
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
}));

export const Kontakt = forwardRef<HTMLDivElement, unknown>((props, ref) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<IKontaktformular>({
    resolver: classValidatorResolver(IKontaktformular),
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const { analytics, functions, cookiesAccepted } = useApp();
  const captchaRef = useRef<ReCAPTCHA>(null);

  const submit = (data: IKontaktformular) => {
    if (!functions) {
      setShowError(true);
      return;
    }

    if (!captchaRef.current) {
      setShowError(true);
      return;
    }

    if (!data.privacyAccepted) {
      setShowError(true);
      return;
    }

    const captchaToken = captchaRef.current.getValue();
    const sendMail = httpsCallable(functions, "sendMail");
    sendMail({ ...data, captchaToken })
      .then(() => {
        reset();
        setShowSuccess(true);
        if (analytics) {
          logEvent(analytics, "kontaktformular_submit", {
            success: true,
          });
        }
        if (captchaRef.current) {
          captchaRef.current.reset();
        }
      })
      .catch((error) => {
        console.log(error);
        setShowError(true);
        if (analytics) {
          logEvent(analytics, "kontaktformular_submit", {
            success: false,
          });
        }
      });
  };

  return (
    <Section ref={ref} id={"kontakt"}>
      <Container>
        <SectionTitle style={{ marginBottom: 20, marginTop: 0 }}>
          Kontakt
        </SectionTitle>
        <Subtitle>Wir freuen uns von dir zu hören!</Subtitle>
        <form onSubmit={handleSubmit(submit)}>
          <FormularWrapper>
            <Label>
              <span>Dein Name</span>
              <Input
                {...register("name", { required: true })}
                required
                placeholder={"Dein Name"}
                type="text"
              />
            </Label>
            <Label>
              <span>Deine E-Mail</span>
              <Input
                {...register("email", { required: true })}
                required
                placeholder={"Deine E-Mail"}
                type="email"
              />
            </Label>
            <FullsizeLabel>
              <span>Deine Nachricht an uns</span>
              <Textarea
                {...register("message")}
                placeholder={"Deine Nachricht"}
                maxLength={1000}
                rows={4}
              />
            </FullsizeLabel>
            <FullsizeLabel
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <CheckboxInput
                type={"checkbox"}
                required
                {...register("privacyAccepted", { required: true })}
              />
              <span>
                Ich habe die{" "}
                <Link
                  href={"/datenschutz"}
                  target={"_blank"}
                  type={"button"}
                  rel="noreferrer"
                >
                  Datenschutzerklärung
                </Link>{" "}
                gelesen und akzeptiere diese.
              </span>
            </FullsizeLabel>
            <ReCAPTCHA
              ref={captchaRef}
              sitekey={"6Lcja_QjAAAAAOtebSy00CeXuxQcjRd2soEo87fY"}
              size={"normal"}
            />
          </FormularWrapper>
          <SubmitButton
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type={"submit"}
          >
            Absenden
          </SubmitButton>
        </form>
        {cookiesAccepted && (
          <Frame
            src={LOCATION}
            title="centre films google maps"
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}
        <Divider />
        <ChefsWrapper>
          <ChefsItem whileHover={{ scale: 1.02 }}>
            <span style={{ color: "#000", fontWeight: "bold" }}>
              Martin Förster
            </span>
            <span style={{ color: "#000", fontWeight: "normal" }}>
              Geschäftsführer
            </span>
            <ChefsLink href={"tel:+491733802800"} style={{ color: "#1a73e8" }}>
              +49 173 3802800
            </ChefsLink>
            <ChefsLink
              href={"mailto:martin@centrefilms.de"}
              style={{ color: "#1a73e8" }}
            >
              martin@centrefilms.de
            </ChefsLink>
          </ChefsItem>
          <ChefsItem whileHover={{ scale: 1.02 }}>
            <span style={{ color: "#000", fontWeight: "bold" }}>
              Felix Roßberg
            </span>
            <span style={{ color: "#000", fontWeight: "normal" }}>
              Geschäftsführer
            </span>
            <ChefsLink href={"tel:+4915222673871"} style={{ color: "#1a73e8" }}>
              +49 152 22673871
            </ChefsLink>
            <ChefsLink
              href={"mailto:felix@centrefilms.de"}
              style={{ color: "#1a73e8" }}
            >
              felix@centrefilms.de
            </ChefsLink>
          </ChefsItem>
        </ChefsWrapper>
      </Container>
      <Alert
        open={showSuccess}
        onDismiss={() => setShowSuccess(false)}
        title={"Vielen Dank!"}
        dismissTitle={"Geht klar!"}
        message={
          "Deine Nachricht ist in unserem System eingegangen und wird\n" +
          "          schnellstmöglich bearbeitet."
        }
      />
      <Alert
        open={showError}
        onDismiss={() => setShowError(false)}
        dismissTitle={"Schade!"}
        title={"Oh Nein!"}
        message={
          "Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte\n" +
          "                versuche es später erneut!"
        }
      />
    </Section>
  );
});
